import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AlertComponent, BodyanalyticsBaseComponent, RouterUrlsEnum } from '@bodyanalytics/ui-core';
import { CustomerProfileCreatePayloadI } from '@bodyanalytics/data-models-ui';
import { RegisterService } from '../../services/register.service';
import { RegisterProfileFormComponent } from '../../components/profile-form/profile-form.component';

/**
 * Create-profile onboarding step (#1760). Reached authenticated (route has
 * AuthGuard; the identity guard also redirects here on a tagged 404). Submits
 * the profile to `POST customer-self-service-api/profile`, which creates the
 * Customer row for the JWT identity, then continues to the member dashboard —
 * whose identity guard now resolves (the row exists) instead of looping.
 */
@Component({
  selector: 'bodyanalytics-register-profile',
  imports: [AlertComponent, RegisterProfileFormComponent],
  templateUrl: './register-profile.component.html',
  styleUrls: ['./register-profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterProfileComponent extends BodyanalyticsBaseComponent {
  private router = inject(Router);
  private registerService = inject(RegisterService);

  /** True while the create call is in flight (disables double-submit). */
  readonly isSubmitting = signal(false);
  /** User-facing error surfaced under the heading — never a silent failure (#1760). */
  readonly errorMessage = signal('');

  update(profile: CustomerProfileCreatePayloadI): void {
    if (this.isSubmitting()) {
      return;
    }
    this.isSubmitting.set(true);
    this.errorMessage.set('');
    this.subscriptions.barrel = this.registerService.createProfile(profile).subscribe({
      // The endpoint is idempotent — an already-existing profile also lands here,
      // so a retry can never dead-end on "already registered".
      next: () => this.router.navigate([`${RouterUrlsEnum.AUTH_DASHBOARD}`]),
      error: () => {
        this.isSubmitting.set(false);
        this.errorMessage.set('We could not save your profile. Please check your details and try again.');
      },
    });
  }
}
